import React, { useEffect, useState } from "react";
import { financeApi as F, inr } from "@/lib/finance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function PaymentDialog({ open, onOpenChange, onDone, presetCustomerId }) {
  const [meta, setMeta] = useState({ payment_modes: [] });
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(presetCustomerId || "");
  const [mode, setMode] = useState("Bank Transfer");
  const [amount, setAmount] = useState("");
  const [tds, setTds] = useState("");
  const [reference, setReference] = useState("");
  const [openInvoices, setOpenInvoices] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { F.meta().then(setMeta); F.customers().then(setCustomers); setCustomerId(presetCustomerId || ""); setAmount(""); setTds(""); setReference(""); setAllocs({}); setOpenInvoices([]); } }, [open, presetCustomerId]);
  useEffect(() => { if (customerId) F.openInvoices(customerId).then(setOpenInvoices); else setOpenInvoices([]); }, [customerId]);

  const settlement = (Number(amount) || 0) + (Number(tds) || 0);
  const allocated = Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0);
  const advance = Math.max(0, settlement - allocated);

  const autoAllocate = () => {
    let remaining = settlement; const next = {};
    for (const inv of openInvoices) { if (remaining <= 0) break; const take = Math.min(remaining, inv.balance_amount); next[inv.id] = Number(take.toFixed(2)); remaining -= take; }
    setAllocs(next);
  };

  const submit = async () => {
    if (settlement <= 0) { toast.error("Enter an amount"); return; }
    if (allocated > settlement + 0.01) { toast.error("Allocation exceeds amount received"); return; }
    const allocations = Object.entries(allocs).filter(([, v]) => Number(v) > 0).map(([invoice_id, v]) => ({ invoice_id, amount: Number(v) }));
    setBusy(true);
    try { const r = await F.createPayment({ customer_id: customerId, mode, amount: Number(amount) || 0, tds: Number(tds) || 0, reference, allocations }); toast.success(`Payment ${r.payment_no} recorded${r.advance > 0 ? ` · advance ${inr(r.advance)}` : ""}`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Record Payment</DialogTitle><DialogDescription>Record a receipt, allocate it across open invoices, or leave the balance as an advance.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId} disabled={!!presetCustomerId}>
                <SelectTrigger data-testid="payment-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.payment_modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Amount received</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">TDS deducted</Label><Input type="number" value={tds} onChange={(e) => setTds(e.target.value)} data-testid="payment-tds-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Txn / cheque no" data-testid="payment-reference-input" /></div>
          </div>
          {customerId && (
            <div className="rounded-md border border-slate-200 p-2">
              <div className="mb-2 flex items-center justify-between"><span className="text-xs font-medium uppercase text-slate-500">Open invoices</span><Button size="sm" variant="outline" onClick={autoAllocate} data-testid="auto-allocate-btn">Auto-allocate</Button></div>
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {openInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`alloc-${inv.invoice_no}`}>
                    <span className="text-slate-700">{inv.invoice_no} <span className="text-xs text-slate-400">(bal {inr(inv.balance_amount)})</span></span>
                    <Input type="number" className="h-8 w-28" value={allocs[inv.id] ?? ""} onChange={(e) => setAllocs({ ...allocs, [inv.id]: e.target.value })} data-testid={`alloc-input-${inv.invoice_no}`} />
                  </div>
                ))}
                {openInvoices.length === 0 && <p className="py-2 text-center text-xs text-slate-400">No open invoices — full amount becomes an advance.</p>}
              </div>
            </div>
          )}
          <div className="flex justify-between rounded-md bg-slate-50 px-3 py-2 text-sm"><span className="text-slate-500">Settlement {inr(settlement)} · Allocated {inr(allocated)}</span><span className="font-medium text-indigo-600">Advance {inr(advance)}</span></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy || settlement <= 0 || !customerId} data-testid="submit-payment-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
